/**
 * The Cloudflare Turnstile widget on the login card (DEPLOYMENT-PLAN.md item
 * 6.6). Rendered only when a site key is configured (config.ts's
 * TURNSTILE_SITE_KEY), so an un-gated build never loads Cloudflare's script.
 *
 * Explicit render (`?render=explicit`) rather than the auto-scan mode: the
 * script is fetched once per document, and this component decides exactly
 * when and where the challenge mounts. Every path that invalidates the token
 * -- solve, error, expiry, an explicit reset after a failed sign-in -- flows
 * through the single `onToken` callback, so the page never holds a token
 * Cloudflare has already retired.
 */
import { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': () => void;
  'expired-callback': () => void;
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** A blocked or hung CDN must not be permanent: without a ceiling the promise
 *  never settles and Sign in stays disabled forever with no explanation. */
const SCRIPT_LOAD_TIMEOUT_MS = 15_000;

/** One shared load per document: two mounts (or a remount) must not inject a
 *  second script tag. Resolves immediately if the API is already present.
 *  A FAILED load clears this cache (and its script element) so the retry
 *  affordance on the login card gets a genuinely fresh attempt -- caching the
 *  rejection would disable sign-in for the life of the page. */
let scriptLoad: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  scriptLoad ??= new Promise<void>((resolve, reject) => {
    if (window.turnstile !== undefined) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;

    const fail = (): void => {
      clearTimeout(timer);
      el.remove();
      scriptLoad = null;
      reject(new Error('turnstile script failed to load'));
    };
    const timer = setTimeout(fail, SCRIPT_LOAD_TIMEOUT_MS);

    el.addEventListener('load', () => {
      clearTimeout(timer);
      resolve();
    });
    el.addEventListener('error', fail);
    document.head.append(el);
  });
  return scriptLoad;
}

export interface TurnstileWidgetProps {
  siteKey: string;
  /** Called with the solved token, or null whenever the token is gone
   *  (error, expiry, reset, or a script that never loaded). */
  onToken: (token: string | null) => void;
  /** Bump to force a fresh challenge -- the login page does this after a
   *  rejected sign-in, since Turnstile tokens are single-use. */
  resetSignal: number;
  /** The script never loaded (blocked, offline, or timed out). The login card
   *  turns this into a human message plus a retry affordance -- silence here
   *  reads as a permanently dead Sign in button. */
  onLoadError?: () => void;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  resetSignal,
  onLoadError,
}: Readonly<TurnstileWidgetProps>): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);

  // Held in refs so a new callback identity never re-renders the challenge
  // (a re-render would throw the user's solved token away).
  const onTokenRef = useRef(onToken);
  const onLoadErrorRef = useRef(onLoadError);
  useEffect(() => {
    onTokenRef.current = onToken;
    onLoadErrorRef.current = onLoadError;
  }, [onToken, onLoadError]);

  useEffect(() => {
    let cancelled = false;
    let id: string | null = null;

    void loadTurnstileScript()
      .then(() => {
        if (cancelled || containerRef.current === null || window.turnstile === undefined) return;
        id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => onTokenRef.current(token),
          'error-callback': () => onTokenRef.current(null),
          'expired-callback': () => onTokenRef.current(null),
        });
        setWidgetId(id);
      })
      .catch(() => {
        if (cancelled) return;
        onTokenRef.current(null);
        onLoadErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      if (id !== null) window.turnstile?.remove(id);
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetSignal === 0 || widgetId === null) return;
    window.turnstile?.reset(widgetId);
    onTokenRef.current(null);
  }, [resetSignal, widgetId]);

  return <Box ref={containerRef} data-testid="turnstile-widget" sx={{ mt: 1.5, minHeight: 65 }} />;
}

export default TurnstileWidget;
